import { AnyTranslationsFn } from "@/types/locale";
import { MenuItem } from "@/types/ui";
import { UseRouteInfo } from "@hooks/pages/use-route-info";
import { buildSidebarMenuItem } from "@hooks/use-feature-menu";
import { removeUndefined } from "@lib/common";
import { rightsActions } from "@lib/rights";
import { AccessToken, UserTokenValue } from "@features/auth/types/access-token";
import routes from "@features/routes";
import { WorkspaceEntityRightsTypes } from "@features/workspaces/types/rights";
import { WorkspaceEntity, WorkspaceOutputView } from "./types/workspace";

const addRights = (workspace: WorkspaceOutputView): WorkspaceEntity =>
  rightsActions.parseActions<WorkspaceOutputView, WorkspaceEntityRightsTypes, WorkspaceEntity>(
    workspace,
    (actions, fns) => ({
      ...fns.canEntityRights(actions, "workspace"),
      canListProjects: fns.canList(actions, "workspace/project"),
      canCreateProject: fns.canCreate(actions, "workspace/project"),
      canListEvents: fns.canList(actions, "workspace/event"),
      canListVenues: fns.canList(actions, "workspace/venue"),
      canCreateVenue: fns.canCreate(actions, "workspace/venue"),
      canListSeasons: fns.canList(actions, "workspace/season"),
      canCreateSeason: fns.canCreate(actions, "workspace/season"),
      canListMembers: fns.canList(actions, "team/workspace-member"),
      canAddMember: fns.can(actions, "team/workspace-member", "register"),
      canFindMember: fns.can(actions, "workspace/contributor", "search"),
    }),
  );

export const workspacesTools = {
  sortByTitle: (a: WorkspaceEntity, b: WorkspaceEntity) => {
    if (!a?.hint || !b?.hint) {
      return 0;
    }
    return ("" + a.hint).localeCompare(b.hint);
  },
  convertToDomainEntity: (workspace: WorkspaceOutputView): WorkspaceEntity => ({
    ...addRights(workspace),
    uiLinks: {
      edit: routes.workspaces.pages.workspace_edit.path({
        idOrSlug: workspace.slug ?? workspace?.id,
      }),
      team: routes.teams.pages.workspace_team.path({
        idOrSlug: workspace.slug ?? workspace?.id,
      }),
    },
  }),
  buildSidebarQuickActionsMenuItems: (
    localeAs: AnyTranslationsFn,
    workspace: WorkspaceEntity | undefined | null,
    pageInfo: UseRouteInfo,
  ): MenuItem[] => {
    const idOrSlug = workspace?.slug ?? workspace?.id;

    if (!idOrSlug || !workspace?.rights.canCreateProject) {
      return [];
    }

    return [
      buildSidebarMenuItem({
        localeAs,
        page: routes.projects.pages.project_create,
        matches: {
          idOrSlug,
        },
        isDisabled: !workspace?.rights.canCreateProject,
        pageInfo,
        isFullButton: true,
      }),
      buildSidebarMenuItem({
        localeAs,
        page: routes.events.pages.event_create_master,
        matches: {
          idOrSlug,
        },
        isDisabled: !workspace?.rights.canListProjects,
        pageInfo,
        isFullButton: true,
      }),
    ];
  },
  buildSidebarMenuItems: (
    localeAs: AnyTranslationsFn,
    workspace: WorkspaceEntity | undefined | null,
    pageInfo: UseRouteInfo,
    user?: UserTokenValue,
    isPrivateMode?: boolean,
  ): MenuItem[] => {
    const idOrSlug = workspace?.slug ?? workspace?.id;
    const globalDisabled = !idOrSlug;

    return removeUndefined([
      isPrivateMode
        ? undefined
        : buildSidebarMenuItem({
            localeAs,
            page: routes.projects.pages.workspace_projects,
            matches: {
              idOrSlug,
            },
            isDisabled: globalDisabled || !workspace?.rights.canListProjects,
            pageInfo,
            burgerItems: [
              buildSidebarMenuItem({
                localeAs,
                page: routes.projects.pages.project_create,
                matches: {
                  idOrSlug,
                },
                isDisabled: !workspace?.rights.canCreateProject,
                pageInfo,
              }),
            ],
          }),
      isPrivateMode
        ? undefined
        : buildSidebarMenuItem({
            localeAs,
            page: routes.contributors.pages.workspace_contributors,
            matches: {
              idOrSlug,
            },
            isDisabled: globalDisabled || !workspace?.rights.canFindMember,
            pageInfo,
          }),
      buildSidebarMenuItem({
        localeAs,
        page: routes.events.pages.workspace_events,
        matches: {
          idOrSlug,
        },
        isDisabled: globalDisabled || !workspace?.rights.canListEvents,
        pageInfo,
        burgerItems: [
          buildSidebarMenuItem({
            localeAs,
            page: routes.events.pages.event_create_master,
            matches: {
              idOrSlug,
            },
            isDisabled: !workspace?.rights.canListProjects,
            pageInfo,
          }),
        ],
      }),
      isPrivateMode
        ? undefined
        : buildSidebarMenuItem({
            localeAs,
            page: routes.venues.pages.workspace_venues,
            matches: {
              idOrSlug,
            },
            isDisabled: globalDisabled || !workspace?.rights.canListVenues,
            pageInfo,
            burgerItems: [
              buildSidebarMenuItem({
                localeAs,
                page: routes.venues.pages.venue_create,
                matches: {
                  idOrSlug,
                },
                isDisabled: !workspace?.rights.canCreateVenue,
                pageInfo,
              }),
            ],
          }),
      isPrivateMode
        ? undefined
        : buildSidebarMenuItem({
            localeAs,
            page: routes.seasons.pages.workspace_seasons,
            matches: {
              idOrSlug,
            },
            isDisabled: globalDisabled || !workspace?.rights.canListSeasons,
            pageInfo,
            burgerItems: [
              buildSidebarMenuItem({
                localeAs,
                page: routes.seasons.pages.season_create,
                matches: {
                  idOrSlug,
                },
                isDisabled: !workspace?.rights.canCreateSeason,
                pageInfo,
              }),
            ],
          }),
      AccessToken.isGlobalAdmin(user) && isPrivateMode
        ? {
            ...buildSidebarMenuItem({
              localeAs,
              page: routes.workspaces.pages.workspace_users,
              matches: {
                idOrSlug,
              },
              isDisabled: globalDisabled || !AccessToken.isGlobalAdmin(user),
              pageInfo,
            }),
            isActive: [
              routes.workspaces.pages.workspace_users.pathTemplate,
              routes.participants.pages.participant_overview.pathTemplate,
              routes.participants.pages.participant_purchases.pathTemplate,
            ].includes(pageInfo?.page?.pathTemplate ?? ""),
          }
        : undefined,
      workspace?.rights?.canListMembers
        ? buildSidebarMenuItem({
            localeAs,
            page: routes.teams.pages.workspace_team,
            matches: {
              idOrSlug,
            },
            isDisabled: globalDisabled || !workspace?.rights?.canListMembers,
            pageInfo,
          })
        : undefined,
    ]) as MenuItem[];
  },
};
